/**
 * Typed client for the vis_nav judge API.
 *
 * Types mirror `source/schemas.py` on the server. Keep them in sync — the server
 * is the source of truth, and its OpenAPI schema is at `/openapi.json`.
 *
 * The base URL comes from `VITE_API_URL` at build time. The previous frontend
 * hardcoded a personal domain into the bundle, which meant the API could not be
 * repointed without rebuilding a frontend whose source had been lost. Never
 * hardcode it here.
 */

import { apiKeyStore } from "@/lib/auth";

export const API_URL: string =
  import.meta.env.VITE_API_URL?.replace(/\/$/, "") || "http://localhost:8000";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GoalTier = "perfect" | "partial" | "failed";

/**
 * Mirrors `SubmissionStatus` in the server's models.py, where a CHECK constraint
 * enforces it. Replaces the old `JobStatus`, whose `pending` is now `queued`.
 */
export type SubmissionStatus = "queued" | "running" | "done" | "failed";

/**
 * Must match `ChallengeType` in the server's models.py, enforced by a CHECK.
 * `dry_run` was retired and folded into `development`.
 */
export type ChallengeType = "development" | "competition";

/**
 * `public` challenges and their leaderboards are readable without a key, so the
 * landing page can show past work to a visitor. `enrolled` restricts both to
 * students of the owning course, and is the default on creation. `staff` restricts
 * them further, to that course's teaching team: a challenge being rehearsed, which
 * its own students neither see in the listing nor can open or run.
 */
export type Visibility = "public" | "enrolled" | "staff";

/**
 * Where a challenge's simulation runs.
 *
 * `client` is the original arrangement: the student runs PyBullet locally and uploads an
 * encrypted recording, which the server can only measure. `server` means the gateway owns
 * the simulation and a WebSocket session *is* the submission, so the trajectory is produced
 * by the side doing the ranking rather than reported by the side being ranked.
 */
export type SimulatorSide = "client" | "server";

/**
 * The lifecycle of one navigation attempt.
 *
 * `pending` is a reservation the SDK has not redeemed yet; it is not an attempt, and the
 * session list omits it. `running` is valid only while a socket is attached; a session
 * cannot outlive the server process, so any row still `running` at startup is reaped to
 * `expired`. Only `scored` has a submission behind it.
 */
export type SessionStatus =
  | "pending"
  | "running"
  | "scored"
  | "aborted"
  | "expired"
  | "failed";

/** True while the session may still change: the page keeps polling. */
export function isSessionActive(status: SessionStatus): boolean {
  return status === "pending" || status === "running";
}

export interface Member {
  email: string;
  name: string;
}

export interface Group {
  id: string;
  name: string;
  members: Member[];
}

/** An academic term. Order by `starts_at`, never by `id`: "2026-fall" sorts
 *  before "2026-spring" alphabetically but comes after it in time. */
export interface Term {
  id: string;
  label: string;
  starts_at: number;
  active: boolean;
}

/**
 * A course offering inside a term. Courses are independent — ROB-UY 3203 and
 * ROB-GY 6203 in one term share no challenges, students or teams.
 */
export interface Course {
  id: string;
  term_id: string;
  code: string;
  title: string;
  active: boolean;
  /** Team policy: students form teams themselves within these; admins are not bound. */
  team_max_size: number;
  teams_open: boolean;
  /** Epoch ms; after it, changes are closed regardless of `teams_open`. */
  teams_lock_at: number | null;
}

/** A course with its term inline, so grouping needs one request. */
export interface CourseWithTerm extends Course {
  term: Term;
}

export interface TeamMember {
  student_id: number;
  email: string;
  name: string;
  /** Epoch ms; 0 for memberships older than the column. */
  joined_at: number;
  /** Who added them, by name; null for an admin or an older row. */
  added_by: string | null;
}

export interface Team {
  id: string;
  name: string;
  created_at: number;
  members: TeamMember[];
}

/** What the caller may do about teams right now. */
export interface TeamPolicy {
  max_size: number;
  /** False once the course is closed or past its lock time; the page goes read-only. */
  open: boolean;
  lock_at: number | null;
}

/** One classmate, for the add-teammate picker. */
export interface Classmate {
  student_id: number;
  email: string;
  name: string;
  team_id: string | null;
  team_name: string | null;
  /** No team yet, so the caller may add them. */
  available: boolean;
}

export interface Me {
  email: string;
  name: string;
  role: StudentRole;
  /** Which cohort the key belongs to. */
  course: CourseWithTerm;
  team: Team | null;
  team_policy: TeamPolicy;
}

/**
 * A rubric, parsed for display. The text is what a TA wrote (see `source/rubric.py` for
 * the language: a hard `goal` on distance, then blocks of `condition -> points`); the rest
 * is the server's pretty-printed reading of it. Evaluated on the server only -- every
 * submission, session and leaderboard row arrives with its points -- so the site never
 * has a second opinion about what a run was worth.
 */
export interface RubricSpec {
  text: string;
  /** The arrival condition, e.g. "distance ≤ 0.2 m". A run that fails it scores nothing. */
  goal: string;
  blocks: RubricBlock[];
  /** What an oracle run scores: distance 0, heading 0, spl 1. */
  max_points: number;
  /** Which of distance, heading, spl, steps the rubric mentions. */
  variables: string[];
}

export interface RubricBlock {
  title: string;
  /** First match pays. */
  rules: { condition: string; points: string; line: number }[];
}

/** The rubric's verdict on one run. */
export interface Score {
  points: number;
  max_points: number;
  /** Whether the goal condition held; false means every block paid nothing. */
  reached: boolean;
  /** Points per block, by title. */
  awarded: Record<string, number>;
  /** The rule that paid in each block (0-based), null for none. */
  matched: Record<string, number | null>;
}

/** What the editor gets back for a draft: parsed, or where it went wrong, plus samples. */
export interface RubricCheck {
  ok: boolean;
  problem: { line: number; col: number; message: string } | null;
  spec: RubricSpec | null;
  preview: { name: string; metrics: Record<string, number | null>; score: Score; tier: GoalTier }[];
}

/**
 * Per-tick Gaussian noise on the commanded speeds, server-simulated only.
 *
 * The sigmas are kept even when `enabled` is false, so toggling does not lose a tuned
 * value. Defaults (0.3 m/s, 0.1 rad/s) are `vis_nav_game`'s NOISY_MOTION values and what
 * the exploration recorder uses.
 */
export interface MotionNoise {
  enabled: boolean;
  /** m/s, per tick */
  linear_sigma: number;
  /** rad/s, per tick */
  angular_sigma: number;
  /**
   * The per-session bias, drawn once from the run's seed and held: the linear and angular
   * speeds are scaled by 1 + N(0, sigma), and while translating the robot turns at
   * curvature * v with curvature ~ N(0, heading_bias_sigma) rad/m. Per-tick noise averages
   * out over a leg; this is what makes dead reckoning drift. Zero is off.
   */
  speed_scale_sigma: number;
  turn_scale_sigma: number;
  /** rad/m */
  heading_bias_sigma: number;
}

export const DEFAULT_MOTION_NOISE: MotionNoise = {
  enabled: false,
  linear_sigma: 0.3,
  angular_sigma: 0.1,
  speed_scale_sigma: 0.03,
  turn_scale_sigma: 0.03,
  heading_bias_sigma: 0.01,
};

/** The server refuses a scale sigma above this: at three sigma the robot would stop or reverse. */
export const MAX_SCALE_SIGMA = 0.3;

/** Sigmas the server would refuse: negative, not a number, or a scale that could reverse the robot. */
export function motionNoiseInvalid(value: MotionNoise): boolean {
  const sigmas = [
    value.linear_sigma,
    value.angular_sigma,
    value.speed_scale_sigma,
    value.turn_scale_sigma,
    value.heading_bias_sigma,
  ];
  return (
    sigmas.some((s) => !Number.isFinite(s) || s < 0) ||
    value.speed_scale_sigma > MAX_SCALE_SIGMA ||
    value.turn_scale_sigma > MAX_SCALE_SIGMA
  );
}

export interface Challenge {
  id: string;
  course_id: string;
  name: string;
  type: ChallengeType;
  visibility: Visibility;
  active: boolean;
  /** Epoch milliseconds, or null for no deadline. */
  deadline: number | null;
  /** Epoch ms before which students get neither the exploration data nor a run; null = open. */
  opens_at: number | null;
  created_at: number;
  show_trajectory: boolean;
  description: string;
  /** Google Drive file id — goes into the student's startup.json. */
  maze_id: string;
  /** md5 of the encrypted maze — also goes into startup.json. */
  maze_md5: string;
  exploration_data_url: string;
  final_submission_link: string;
  /** Whether each team hands in a report (difficulty, slides PDF, agent code) on this site. */
  collects_report: boolean;
  /** Epoch milliseconds, or null for no report deadline. Separate from `deadline`: the report is due after the runs. */
  report_deadline: number | null;
  /** The slide deck teams fill in for the report, or "" if none is given. */
  report_template_url: string;
  rubric: string;
  rubric_spec: RubricSpec;

  /**
   * Where the simulation runs, and therefore which of two entirely different workflows
   * a student follows: download a maze and upload a recording, or open a session with
   * the SDK.
   *
   * Fixed when the challenge is created and never patchable. Flipping it would migrate
   * nothing — a challenge with uploaded recordings would keep those scores and start
   * collecting new ones from a different physics engine, and the leaderboard would rank
   * the two against each other with nothing saying so.
   */
  simulator: SimulatorSide;
  /** Server-simulated only. Per team for a competition, per student for practice; null is unlimited. */
  max_attempts: number | null;
  /** Server-simulated only. Replaces the old 60-minute wall clock. */
  max_steps: number;
}

/** Admin view: adds the fields deliberately withheld from students. */
export interface AdminChallenge extends Challenge {
  /**
   * Server-simulated only. Not public: the motion model is not announced to students,
   * neither whether a run is noisy nor by how much. The exploration dataset moves the same
   * way, so what they need to know they measure, as with a robot.
   */
  motion_noise: MotionNoise;
  /**
   * Whether students on the server's currently configured build can decrypt this
   * challenge's maze.
   *
   * The tag itself is deliberately not exposed, not even here. It is the only entropy
   * in the key, and being an admin endpoint is not a reason to send a secret to a
   * browser — it reached the deployed bundle once, as the placeholder on the form
   * field that used to collect it.
   *
   * `false` is expected and harmless for a challenge from an earlier build, and a
   * problem for an active one: every submission to it fails. `null` means the server
   * has no COURSE_TAG configured, so the question has no answer.
   */
  readable_by_current_build: boolean | null;
  /** Server-simulated only: the library maze the worker serves; null until chosen. */
  bundle_id: string | null;
  /** Per team on every challenge. */
  max_concurrent_sessions: number;
}

/**
 * Where a run ended relative to the target. Present only for a run that reached the goal:
 * one that missed is told that it missed, not by how much, so the numbers cannot steer a
 * search. `heading_deg` is null for an upload, whose maze has no target heading.
 */
export interface Arrival {
  distance_m: number;
  heading_deg: number | null;
}

export interface LeaderboardRow {
  rank: number;
  group: Group | null;
  best_member: Member;
  arrival: Arrival | null;
  /**
   * Success weighted by path length, 0..1: the shortest route's steps over the run's,
   * capped at 1, and 0 for a miss. Null only for an upload, whose maze has no grid.
   */
  spl: number | null;
  nav_steps: number;
  /**
   * The run's wall clock, whichever there was: the session's for a server-simulated run,
   * the student's game's for an upload. Shown, never ranked.
   */
  wall_s: number | null;
  /** What the rubric awarded, out of what it could have. What ranks. */
  points: number;
  max_points: number;
  submitted_at: number;
  reached_goal: GoalTier;
}

/**
 * One attempt, at any stage of its life.
 *
 * Replaces the previous `Job` and `SubmissionEntry` pair, which described two
 * server documents holding overlapping copies of the same four fields — and
 * which had diverged in the real data, so the two endpoints could report
 * different scores for one run. There is a single row now.
 *
 * The verdict is populated only when `status` is `done`; `error_msg` only when
 * `failed`. The server enforces exactly that correspondence, so a `done`
 * submission's `score` is never null in practice.
 */
export interface Submission {
  job_id: string;
  challenge_id: string;
  email: string;
  submitted_at: number;
  status: SubmissionStatus;
  nav_steps: number | null;
  nav_time_s: number | null;
  /** The session's wall clock for a server-simulated run, `nav_time_s` for an upload. */
  wall_s: number | null;
  /** The rubric's verdict and badge, once scored; recomputed server-side from the current rubric. */
  score: Score | null;
  goal_tier: GoalTier | null;
  /** Null for an upload, and until scored. */
  spl: number | null;
  /** Only for a run that reached the goal. */
  arrival: Arrival | null;
  error_msg: string | null;
  /** The sim session that scored this row; null for an uploaded recording. */
  session_id: string | null;
}

/** A submission as a TA sees it: with the measurements a student is not shown. */
export interface AdminSubmission extends Submission {
  /** Metres from the target, whether or not the run reached the goal. */
  trans_error: number | null;
  /** Degrees off the target heading; null for an upload. */
  rot_error: number | null;
  /** SPL before success is applied: the shortest route's steps over the run's, capped at 1. */
  path_efficiency: number | null;
}

/** The score a session produced, present only once it is `scored`. */
export interface SimResult {
  goal_tier: GoalTier;
  spl: number;
  nav_steps: number;
  score: Score;
  /** Only for a run that reached the goal. */
  arrival: Arrival | null;
}

/**
 * Counters read from the gateway's memory while a session is `running`.
 *
 * No RTT floor: that is how the think-time report is checked, and it is admin-only.
 * `connected` says whether a client is attached right now; `grace_ends_at` is set while
 * one is not, and is when the session ends if nobody comes back.
 */
export interface SimLive {
  steps_used: number;
  steps_left: number;
  think_mean_ms: number | null;
  connected: boolean;
  grace_ends_at: number | null;
  /** Epoch ms of the newest camera frame; fetch `/frame` again when it changes. */
  last_frame_at: number | null;
}

/**
 * One navigation attempt, as a student sees their own.
 *
 * This -- not the submission -- is the unit of a server-simulated challenge's history. A
 * run that was aborted or timed out never gets a submission, but it happened and it spent
 * an attempt, so the site lists sessions and treats the submission as one of its fields.
 */
export interface SimSession {
  id: string;
  challenge_id: string;
  started_at: number;
  connected_at: number | null;
  ended_at: number | null;
  /** Only while `pending`: when the unredeemed reservation lapses. */
  expires_at: number | null;
  status: SessionStatus;
  end_reason: string | null;
  steps_used: number;
  job_id: string | null;
  result: SimResult | null;
  timing: SessionTiming;
  live: SimLive | null;
}

/** A camera frame from a running session, in whichever encoding the worker produced. */
export type SimFrame =
  | { encoding: "jpeg"; blob: Blob }
  | { encoding: "raw"; shape: [number, number, number]; data: Uint8ClampedArray };

/**
 * Where a session's wall clock went, in milliseconds. Four clocks for four questions:
 * `sim_ms` is how long the robot moved (steps × 10 ms; the only one that ranks, and only
 * because it is nav_steps in other units); `wall_ms` how long the student sat there;
 * `think_*` the student's code as the gateway measured it (network cancelled), with the
 * SDK's own report of the same quantity as `reported_*`; `server_ms` our machine. The
 * remainder — network and time spent not stepping — is `wall - think - server`, computed
 * client-side because it is a remainder.
 */
export interface SessionTiming {
  sim_ms: number;
  wall_ms: number | null;
  think_ms: number;
  think_mean_ms: number | null;
  think_p95_ms: number | null;
  think_max_ms: number;
  reported_ms: number;
  reported_mean_ms: number | null;
  reported_max_ms: number;
  server_ms: number;
  steps_measured: number;
}

/**
 * Admin view, adding the two fields deliberately withheld from students.
 *
 * `think_server_ms` is the gateway's own measurement, taken from two arrival times so that
 * latency and image transfer cancel. It exists to check `think_total_ms`, and telling a
 * student the figure their client is compared against would be telling them what to forge.
 */
export interface TeamRef {
  id: string;
  name: string;
}

/**
 * A session for a TA: who ran it and their team, the raw distance and heading at check-in
 * (a miss's included, which the student is not told), and the verdict on the SDK's
 * self-reported think time.
 */
export interface AdminSimSession extends SimSession {
  email: string;
  name: string;
  team: TeamRef | null;
  trans_error: number | null;
  rot_error: number | null;
  rtt_min_ms: number | null;
  timing_suspect: boolean;
  worker_id: string;
  sdk_version: string;
}

/** What a student needs to plan an attempt. */
export interface SimQuota {
  challenge_id: string;
  simulator: SimulatorSide;
  attempts_used: number;
  attempts_allowed: number | null;
  /** Whose budget `attempts_*` is: the team's for a competition, the student's for practice. */
  attempts_per: "student" | "team";
  max_steps: number;
  /** Per team, which is why `running` can be non-zero without any run of your own. */
  max_concurrent_sessions: number;
  running: number;
  /** The caller's unredeemed reservation, if the SDK has been told to connect but has not. */
  reservation: { session_id: string; started_at: number; expires_at: number } | null;
  /**
   * Why a run cannot start now, in the student's words; null when one can. A competition
   * takes runs only from a team, and only once the course has closed team changes.
   */
  blocked: string | null;
}

export interface TrajectoryPoint {
  phase: "exploration" | "navigation";
  x: number;
  y: number;
  t: number;
}

export interface Trajectory {
  target: { x: number; y: number };
  points: TrajectoryPoint[];
  /** Points before downsampling; greater than points.length if thinned. */
  total_points: number;
}

/**
 * True while a submission is still being scored.
 *
 * There are two unfinished states, `queued` and `running`, where the previous API
 * had one (`pending`). Comparing against a single literal is therefore no longer
 * correct, and would silently treat a running job as finished — so the check lives
 * here rather than being repeated at each call site.
 */
export function isPending(status: SubmissionStatus): boolean {
  return status === "queued" || status === "running";
}

/** True once scoring has finished, either way. Use as a polling stop condition. */
export function isSettled(status: SubmissionStatus): boolean {
  return !isPending(status);
}

/** [model_type, texture_index, x, y, z, roll, pitch, yaw] */
export type WallPanel = number[];

export interface StudentKeyEntry {
  /** The key itself. Returned in plaintext because it is stored that way. */
  key: string;
  /** The enrollment the key belongs to; what `deleteStudent` takes. */
  student_id: number;
  email: string;
  name: string;
  course_id: string;
  /** Epoch milliseconds. Was a 'YYYY-MM-DD' string in the JSON layout. */
  created_at: number;
  active: boolean;
  /** Staff hold keys to dry-run challenges; they are kept off leaderboards. */
  role: StudentRole;
  /** The Slack account this enrollment is, once the sync has recorded it. */
  slack_user_id: string | null;
}

export type StudentRole = "student" | "staff";

export interface GeneratedKey {
  email: string;
  name: string;
  key: string;
}

export interface KeyGenerateResponse {
  created: GeneratedKey[];
  skipped: string[];
}

export interface AdminGroup {
  id: string;
  course_id: string;
  name: string;
  member_emails: string[];
  members: TeamMember[];
  created_at: number;
  /** The student who formed it, by name; null for an admin-made team. */
  created_by: string | null;
}

export interface Health {
  status: "ok";
  terms: number;
  courses: number;
  challenges: number;
  students: number;
  groups: number;
  submissions: number;
  data_dir: string;
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

/** One of the team's finished runs on a challenge, as the report form offers it. */
export interface ReportRun {
  id: string;
  started_at: number;
  status: SessionStatus;
  /** Who on the team ran it. */
  run_by: string;
  /** Under the current rubric; null for a run that was not scored. */
  points: number | null;
  max_points: number | null;
  reached: boolean;
}

/** A team's report for one challenge, as the team sees it. */
export interface Report {
  id: string;
  challenge_id: string;
  /** Null once the team has been disbanded; the record stays. */
  team_id: string | null;
  /** As it was at the last hand-in. */
  team_name: string;
  /** Who was on the team at the last hand-in. */
  members: Member[];
  /** 1 (very easy) to 5 (very hard), the team's own rating. */
  difficulty: number;
  /** The run the team wants looked at, or null. */
  run: ReportRun | null;
  /** What failed, and where. */
  problems: string;
  repo_url: string;
  video_url: string;
  file_name: string;
  file_size: number;
  /** my_agent.py, or a .zip. */
  code_file_name: string;
  code_file_size: number;
  first_submitted_at: number;
  submitted_at: number;
  submitted_by: string | null;
  revisions: number;
  /** First handed in after the report deadline. */
  late: boolean;
}

/** The report card on a challenge page. `reason` says why `can_submit` is false. */
export interface ReportState {
  collects_report: boolean;
  report_deadline: number | null;
  report_template_url: string;
  report: Report | null;
  /** The team's finished runs, newest first: the choices for `session_id`. */
  runs: ReportRun[];
  can_submit: boolean;
  reason: string | null;
}

export interface HandInReport {
  difficulty: number;
  /** One of `ReportState.runs`; required once the team has one, "" otherwise. */
  session_id: string;
  problems: string;
  repo_url: string;
  video_url: string;
  /** The slides as a PDF. Required the first time; omit to keep the one on record. */
  file?: File | null;
  /** my_agent.py or a .zip. Required the first time; omit to keep the one on record. */
  code?: File | null;
}

export interface AdminReport extends Report {
  /** The team's name now, or null if it no longer exists. */
  current_team_name: string | null;
}

export interface ReportChallenge {
  id: string;
  name: string;
  type: ChallengeType;
  deadline: number | null;
  report_deadline: number | null;
}

export interface ReportStudentRow {
  student_id: number;
  email: string;
  name: string;
  team_name: string | null;
  /** challenge id -> report id, or null where nothing covers this student yet. */
  reports: Record<string, string | null>;
}

export interface ReportsOverview {
  course: CourseWithTerm;
  challenges: ReportChallenge[];
  reports: AdminReport[];
  students: ReportStudentRow[];
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

/** An error carrying the HTTP status, so callers can branch on 401/403/404. */
export class ApiError extends Error {
  // Declared explicitly rather than as a constructor parameter property, which
  // `erasableSyntaxOnly` in tsconfig disallows.
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }

  /** Credentials missing or rejected. */
  get isUnauthorized() {
    return this.status === 401;
  }

  /** Authenticated, but not allowed. */
  get isForbidden() {
    return this.status === 403;
  }

  get isNotFound() {
    return this.status === 404;
  }

  /** Destructive action needs an explicit confirmation token. */
  get needsConfirmation() {
    return this.status === 428;
  }

  /**
   * The admin API is disabled because the server has no ADMIN_KEY configured.
   * Distinct from 401 on purpose: the credential is not the problem.
   */
  get isNotConfigured() {
    return this.status === 503;
  }
}

type Auth = { apiKey?: string | null; adminKey?: string | null };

/**
 * Three states, not two.
 *
 * `undefined` — omitted — means "whatever this browser is signed in as", read from the
 * store. `null` means "send nothing, deliberately". A string is used as given, which is what
 * lets the sign-in dialog validate a candidate key before storing it.
 *
 * Defaulting to the stored key is the fix for a whole class of bug rather than a
 * convenience. Four of the six call sites that *could* pass a key did not, so an `enrolled`
 * challenge's leaderboard 401'd, its name came out blank in the submission list, and its
 * submit page could not find it -- on pages only reachable while signed in, with the key
 * sitting in a hook one line above. An optional argument that must be remembered every time
 * will be forgotten; the endpoints that take a *required* key had no such bugs, because the
 * compiler asked.
 *
 * The admin key is deliberately not defaulted. Every admin call takes it as a required
 * parameter, so omission is a type error rather than a runtime surprise, and auto-attaching
 * it would send a credential that can delete challenges to every public endpoint too.
 */
function authHeaders({ apiKey, adminKey }: Auth = {}): HeadersInit {
  const headers: Record<string, string> = {};
  const student = apiKey === undefined ? apiKeyStore.get() : apiKey;
  if (student) headers["x-api-key"] = student;
  if (adminKey) headers["x-admin-key"] = adminKey;
  return headers;
}

/** FastAPI returns `detail` as either a string or a validation-error array. */
function extractDetail(body: unknown, status: number): string {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const parts = detail
        .map((d) => {
          if (d && typeof d === "object" && "msg" in d) {
            const loc = Array.isArray((d as { loc?: unknown[] }).loc)
              ? (d as { loc: unknown[] }).loc.slice(1).join(".")
              : "";
            const msg = String((d as { msg: unknown }).msg);
            return loc ? `${loc}: ${msg}` : msg;
          }
          return String(d);
        })
        .filter(Boolean);
      if (parts.length) return parts.join("; ");
    }
  }
  return `Request failed (HTTP ${status})`;
}

/**
 * One fetch, with the key attached and errors turned into ApiError.
 *
 * Exported for `src/legacy/api.ts`, which adds the upload-and-poll endpoints of a
 * client-simulated challenge without those living here.
 */
export async function request<T>(
  path: string,
  init: RequestInit = {},
  auth: Auth = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...init,
      headers: { ...authHeaders(auth), ...init.headers },
    });
  } catch (caught) {
    // A caller that cancelled its own request (a superseded live preview) is not an outage.
    if (caught instanceof DOMException && caught.name === "AbortError") throw caught;
    // Distinguish "server unreachable" from "server said no". The production API
    // has been down before while the static frontend stayed up, and a generic
    // error message made that indistinguishable from a bad key.
    throw new ApiError(
      0,
      `Cannot reach the API at ${API_URL}. It may be offline.`,
    );
  }

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, extractDetail(body, response.status));
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * A DELETE without `confirm`, which the server answers with 428 and a description of
 * what would be destroyed. That description is the return value; `null` means the
 * server went ahead without asking, which none of these routes do.
 */
async function previewDelete(path: string, adminKey: string): Promise<string | null> {
  try {
    await request(path, { method: "DELETE" }, { adminKey });
    return null;
  } catch (error) {
    if (error instanceof ApiError && error.needsConfirmation) return error.message;
    throw error;
  }
}

/**
 * A file behind a key, as an object URL a link or `window.open` can use. A browser cannot
 * put a header on a plain link, so the PDF is fetched here and handed over locally.
 */
async function fileUrl(path: string, auth: Auth): Promise<string> {
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, { headers: authHeaders(auth) });
  } catch {
    throw new ApiError(0, `Cannot reach the API at ${API_URL}. It may be offline.`);
  }
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, extractDetail(body, response.status));
  }
  return URL.createObjectURL(await response.blob());
}

function jsonBody(data: unknown): RequestInit {
  return {
    body: JSON.stringify(data),
    headers: { "Content-Type": "application/json" },
  };
}

// ---------------------------------------------------------------------------
// Public
// ---------------------------------------------------------------------------

export const publicApi = {
  health: () => request<Health>("/health"),

  terms: () => request<Term[]>("/terms"),

  /** Every offering, each carrying its term, for grouping on the landing page. */
  courses: (termId?: string) =>
    request<CourseWithTerm[]>(
      `/courses${termId ? `?term_id=${encodeURIComponent(termId)}` : ""}`,
    ),

  /** Public challenges, plus the caller's own course when signed in. */
  challenges: () => request<Challenge[]>("/challenges"),

  /**
   * One challenge by id.
   *
   * Not `challenges().find()`. That inherited the listing's two filters — `enrolled`
   * challenges are absent without a key, and inactive ones are absent entirely — so the
   * detail page showed "Challenge not available" both for a mistyped id and for a
   * challenge the caller simply had not signed in for, and could not open a closed
   * challenge at all even though submission history links to it.
   */
  challenge: (challengeId: string) =>
    request<Challenge>(`/challenges/${challengeId}`),

  leaderboard: (challengeId: string) =>
    request<LeaderboardRow[]>(`/leaderboard/${challengeId}`),
};

// ---------------------------------------------------------------------------
// Student
// ---------------------------------------------------------------------------

export const studentApi = {
  me: (apiKey: string) => request<Me>("/me", {}, { apiKey }),

  /**
   * Teams are formed by adding classmates -- no create, no join. The first classmate
   * you add makes the team; whoever you add can leave. Every call returns the new `/me`.
   */
  classmates: (apiKey: string) => request<Classmate[]>("/classmates", {}, { apiKey }),
  /** A team of just the caller; classmates can be added to it afterwards. */
  formTeamAlone: (apiKey: string) => request<Me>("/team", { method: "POST" }, { apiKey }),
  addTeammate: (studentId: number, apiKey: string) =>
    request<Me>("/team/members", { method: "POST", ...jsonBody({ student_id: studentId }) }, { apiKey }),
  leaveTeam: (apiKey: string) => request<Me>("/team/members/me", { method: "DELETE" }, { apiKey }),
  renameTeam: (name: string, apiKey: string) =>
    request<Me>("/team", { method: "PATCH", ...jsonBody({ name }) }, { apiKey }),

  walls: (challengeId: string, apiKey: string) =>
    request<WallPanel[]>(`/challenges/${challengeId}/walls`, {}, { apiKey }),

  /** Every attempt, newest first. Includes failures, which the old API omitted. */
  submissions: (challengeId: string, apiKey: string) =>
    request<Submission[]>(`/submissions/${challengeId}`, {}, { apiKey }),

  /** How many attempts are left, and whether the team already has one running. */
  simQuota: (challengeId: string, apiKey: string) =>
    request<SimQuota>(`/sim/${challengeId}/quota`, {}, { apiKey }),

  /** The student's own sessions, newest first. Reservations (`pending`) are omitted. */
  simSessions: (challengeId: string, apiKey: string) =>
    request<SimSession[]>(`/sim/${challengeId}/sessions`, {}, { apiKey }),

  /** One session, with `live` counters while it runs. Visible to the student's team. */
  simSession: (sessionId: string, apiKey: string) =>
    request<SimSession>(`/sim/sessions/${sessionId}`, {}, { apiKey }),

  /**
   * End a running session from its page, unscored. The attempt stays spent.
   *
   * The student's own or a teammate's -- the run holds one of the team's slots. The SDK
   * driving it is told and its next message is refused.
   */
  abortSimSession: (sessionId: string, apiKey: string) =>
    request<SimSession>(`/sim/sessions/${sessionId}/abort`, { method: "POST" }, { apiKey }),

  /**
   * The newest camera frame of a running session, or null once it has ended.
   *
   * Not `request()`: the body is an image, not JSON, and a 404 here is the normal end of
   * a run rather than an error worth surfacing.
   */
  simFrame: async (sessionId: string, apiKey: string): Promise<SimFrame | null> => {
    const response = await fetch(`${API_URL}/sim/sessions/${sessionId}/frame`, {
      headers: authHeaders({ apiKey }),
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new ApiError(response.status, `Frame request failed (HTTP ${response.status})`);
    }
    if (response.headers.get("content-type")?.startsWith("image/")) {
      return { encoding: "jpeg", blob: await response.blob() };
    }
    const shape = response.headers
      .get("x-frame-shape")
      ?.split(",")
      .map((n) => Number(n));
    if (!shape || shape.length !== 3) return null;
    return {
      encoding: "raw",
      shape: shape as [number, number, number],
      data: new Uint8ClampedArray(await response.arrayBuffer()),
    };
  },

  submission: (challengeId: string, submissionId: string, apiKey: string) =>
    request<Submission>(
      `/challenges/${challengeId}/submissions/${submissionId}`,
      {},
      { apiKey },
    ),

  trajectory: (jobId: string, apiKey: string, maxPoints?: number) =>
    request<Trajectory>(
      `/trajectory/${jobId}/data${maxPoints === undefined ? "" : `?max_points=${maxPoints}`}`,
      {},
      { apiKey },
    ),

  /** The caller's team's report for a challenge, what is due, and whether they may hand in now. */
  report: (challengeId: string, apiKey: string) =>
    request<ReportState>(`/challenges/${challengeId}/report`, {}, { apiKey }),

  /** Hand in, or replace before the deadline. Returns the new state. */
  handInReport: (challengeId: string, input: HandInReport, apiKey: string) => {
    const form = new FormData();
    form.append("difficulty", String(input.difficulty));
    form.append("session_id", input.session_id);
    form.append("problems", input.problems);
    form.append("repo_url", input.repo_url);
    form.append("video_url", input.video_url);
    if (input.file) form.append("file", input.file);
    if (input.code) form.append("code", input.code);
    return request<ReportState>(
      `/challenges/${challengeId}/report`,
      { method: "POST", body: form },
      { apiKey },
    );
  },

  /** The team's PDF as an object URL; revoke it when done. */
  reportFileUrl: (challengeId: string, apiKey: string) =>
    fileUrl(`/challenges/${challengeId}/report/file`, { apiKey }),

  /** The team's code as an object URL; revoke it when done. */
  reportCodeUrl: (challengeId: string, apiKey: string) =>
    fileUrl(`/challenges/${challengeId}/report/code`, { apiKey }),
};

// ---------------------------------------------------------------------------
// Server-simulated challenge setup (mirrors vis_nav_sim_rs/MAP_TOOLS.md)
// ---------------------------------------------------------------------------

export interface CameraSpec {
  width: number;
  height: number;
  fx: number;
  fy: number;
  cx: number;
  cy: number;
  near: number;
  far: number;
  height_offset: number;
}

export interface Grid {
  rows: number;
  cols: number;
  cell_size: number;
  free_cells: number;
  /** Row 0 first; `#` wall, `.` free. Row 0 is the *bottom* of the picture (+y up). */
  cells: string[];
}

export type Cell = [number, number];

/** A maze that does not exist yet: the grid and markers `generate` would produce. */
export interface MazePreview {
  /** The seed used, so a preview made from a random one can be created exactly as seen. */
  seed: number;
  scene_sha256: string;
  grid: Grid;
  spawn: { cell: Cell; x: number; y: number; yaw: number };
  target: { cell: Cell; x: number; y: number; reachable: boolean };
  panels?: number | null;
}

/** A library maze: the row plus what its manifest and dataset directory say. */
export interface Maze {
  id: string;
  name: string;
  created_at: number;
  scene_sha256: string;
  notes: string;
  present: boolean;
  source?: Record<string, unknown> | null;
  /** `cells` only on the detail route; the listing is light. */
  grid?: (Omit<Grid, "cells"> & { cells?: string[] }) | null;
  spawn?: { cell: Cell; x: number; y: number; yaw: number } | null;
  target?: { cell: Cell; x: number; y: number; yaw_deg: number; reachable: boolean } | null;
  /** The oracle's route from spawn to target; `oracle_steps` is the numerator of every SPL. */
  shortest_path?: { cells: number; metres: number; turn_deg: number; oracle_steps: number } | null;
  panels?: number | null;
  camera?: CameraSpec | null;
  dataset_state: DatasetState;
  used_by: { id: string; name: string }[];
}

export interface GenerateMazeInput {
  name?: string;
  notes?: string;
  rows?: number;
  cols?: number;
  seed?: number;
  room_density?: number;
  max_room_size?: number;
  target_min_distance?: number;
  target_max_distance?: number;
  camera?: Partial<CameraSpec> | null;
}

export interface Waypoints {
  start: Cell;
  target: Cell;
  trajectories: Cell[][];
}

export interface PlanInput {
  waypoints?: Waypoints;
  routes?: number;
  seed?: number;
}

export interface PlannedRoute {
  index: number;
  waypoints: Cell[];
  path: Cell[];
  cells: number;
  unreachable_legs: number;
}

export interface Coverage {
  covered: number;
  free: number;
  fraction: number;
  /** `[row, col, visits]` */
  counts: [number, number, number][];
}

export interface Plan {
  routes: PlannedRoute[];
  coverage: Coverage;
}

export interface RecordInput extends PlanInput {
  capture_every?: number;
  /** Same model as a challenge's `motion_noise`, so the dataset matches what students navigate under. */
  noisy_motion?: boolean;
  linear_sigma?: number;
  angular_sigma?: number;
  speed_scale_sigma?: number;
  turn_scale_sigma?: number;
  heading_bias_sigma?: number;
  jpeg_quality?: number;
  jpeg_chroma?: "444" | "420";
}

export type DatasetState = "none" | "recording" | "done" | "failed";

export interface DatasetStatus {
  state: DatasetState;
  /** recording: the last progress event from the tool */
  event?: string;
  index?: number;
  cells_done?: number;
  cells_total?: number;
  frames?: number | null;
  routes?: number | null;
  error?: string;
  /** done */
  elapsed_s?: number | null;
  coverage?: { covered: number; free: number; fraction: number } | null;
  trajectories?: { index: number; frames: number; cells: number; stuck_cells: number }[];
  stuck_cells?: number;
  zip_bytes?: number | null;
  previews?: string[];
  student_files?: number;
  url?: string;
}

export interface WorkerOptionsInput {
  max_sessions?: number;
  jpeg_quality?: number;
  jpeg_chroma?: "444" | "420";
  msaa?: 1 | 4;
}

export interface WorkerStatus {
  enabled: boolean;
  process: {
    state: "stopped" | "starting" | "running" | "failed";
    pid?: number | null;
    worker_id?: string | null;
    socket?: string | null;
    uptime_s?: number | null;
    restarts: number;
    last_error?: string | null;
    stderr_tail?: string[];
    options?: WorkerOptionsInput;
  };
  probe: {
    reachable: boolean;
    reason?: string;
    worker_id?: string;
    worker_version?: string;
    scene_sha256?: string;
    scene_matches?: boolean;
    max_sessions?: number;
    open_sessions?: number;
    max_repeat?: number;
  };
  command: string;
}

// ---------------------------------------------------------------------------
// Admin
// ---------------------------------------------------------------------------

export interface CreateChallengeInput {
  course_id: string;
  name: string;
  type: ChallengeType;
  /**
   * Required, with no default, because the server has none either: the mode is
   * irreversible and decides what every other field means.
   */
  simulator: SimulatorSide;
  visibility?: Visibility;
  /** Client-simulated only, and required there. The server rejects it for `server`. */
  maze_id?: string;
  description: string;
  /** Client-simulated: the Drive link. Server-simulated: left empty and filled in when a dataset is recorded. */
  exploration_data_url?: string;
  /** Client-simulated only, and required there. The server refuses one for `server`. */
  maze_file?: File;
  show_trajectory?: boolean;
  final_submission_link?: string;
  /** Teams hand in the report on the challenge page; a graded challenge then needs no form link. */
  collects_report?: boolean;
  /** The rubric text; omitted means distance only at the usual 0.1 / 0.2 m. */
  rubric?: string;
  /** Server-simulated only. The server rejects all three for `client`. */
  max_attempts?: number | null;
  max_concurrent_sessions?: number;
  max_steps?: number;
  /** Server-simulated only: the library maze to run in. Can still be changed on the setup page until someone attempts the challenge. */
  bundle_id?: string;
  /** Epoch milliseconds; omit or null for no deadline. */
  /** Epoch milliseconds: students get the data and runs from then on. */
  opens_at?: number | null;
  deadline?: number | null;
  /**
   * Server-simulated only; omitted means the server's default (on). The server rejects
   * `true` for `client`.
   */
  motion_noise?: boolean;
  noise_linear_sigma?: number;
  noise_angular_sigma?: number;
  noise_speed_scale_sigma?: number;
  noise_turn_scale_sigma?: number;
  noise_heading_bias_sigma?: number;
}

export interface PatchChallengeInput {
  name?: string;
  active?: boolean;
  visibility?: Visibility;
  /** Epoch milliseconds. Send null to clear. */
  deadline?: number | null;
  /** Epoch milliseconds. Send null to open now. */
  opens_at?: number | null;
  show_trajectory?: boolean;
  description?: string;
  maze_id?: string;
  exploration_data_url?: string;
  final_submission_link?: string;
  collects_report?: boolean;
  /** Epoch milliseconds. Send null for no report deadline. */
  report_deadline?: number | null;
  /** http(s) link to the slide template, or "" for none. */
  report_template_url?: string;
  rubric?: string;
  /** Server-simulated only: the library maze. Refused once the challenge has been attempted. */
  bundle_id?: string | null;

  /**
   * The three limits are patchable — an attempt budget is exactly the thing that needs
   * adjusting once you see how a cohort uses it. Lowering `max_steps` does not disturb
   * sessions already running: the gateway snapshots the limits at admission.
   *
   * `simulator` is deliberately absent. It is fixed at creation.
   */
  max_attempts?: number | null;
  max_concurrent_sessions?: number;
  max_steps?: number;
  /** Server-simulated only. Snapshotted at admission, so a change never affects a run in progress. */
  motion_noise?: MotionNoise;
}

/** Who the server's configured Slack bot token belongs to. */
export interface SlackAuth {
  team: string;
  bot_user: string;
  bot_user_id: string;
  /** The workspace's URL, for linking to its admin pages. */
  url: string;
}

/**
 * What one person's two sides disagree about. See `SlackSyncEntry` on the server for the
 * full account; in short:
 * - `new` — active in Slack, not enrolled. Proposes enrolment with `proposed_role`.
 * - `left` — enrolled with a working key, deactivated (or gone) in Slack. Proposes
 *   disabling their keys. Never raised for staff.
 * - `email_changed` — same Slack account, different profile address.
 * - `link` — enrolled from a CSV, no Slack id yet, and an active account matches.
 * - `missing` — enrolled, no Slack account matches. Nothing to do from here.
 * - `ok` — in agreement.
 * - `staff` — the teaching team, listed and never acted on.
 * - `unmappable` — an active account with no email (usually a guest).
 */
export type SyncVerdict = "new" | "left" | "email_changed" | "link" | "missing" | "ok" | "staff" | "unmappable";

export interface SlackSyncEntry {
  verdict: SyncVerdict;
  reason: string;
  slack_user_id: string | null;
  slack_display_name: string;
  slack_email: string | null;
  slack_is_admin: boolean;
  slack_deactivated: boolean;
  student_id: number | null;
  email: string | null;
  name: string;
  role: StudentRole | null;
  has_active_key: boolean;
  proposed_role: StudentRole | null;
  also_enrolled_in: string[];
}

export interface SlackSyncPreview {
  entries: SlackSyncEntry[];
  counts: Record<SyncVerdict, number>;
  /** Disabled here, still active in Slack: for a human with Slack's admin page open. */
  slack_todo: SlackSyncEntry[];
}

export type SlackSyncAction =
  | { action: "enrol"; slack_user_id: string; email: string; name: string; role: StudentRole }
  | { action: "deactivate" | "link"; slack_user_id: string; student_id: number }
  | { action: "update_email"; slack_user_id: string; student_id: number; email: string };

export interface SlackSyncApplyResponse {
  created: GeneratedKey[];
  deactivated: string[];
  updated: string[];
  linked: string[];
  skipped: { slack_user_id: string; reason: string }[];
}

export interface SlackDmRecipient {
  email: string;
  name: string;
  role: StudentRole;
  slack_user_id: string | null;
  text: string;
  /** Variables the message referenced but nothing supplied. Blocks sending. */
  missing: string[];
  /** Why this recipient cannot be reached at all, if they cannot. */
  blocked: string;
}

export interface SlackDmPreview {
  recipients: SlackDmRecipient[];
  /** Variables the template uses, in order of first appearance. */
  variables: string[];
  /** Addresses in the uploaded values matching no enrolled student. */
  unknown_value_keys: string[];
}

export interface SlackDmResult {
  email: string;
  sent: boolean;
  error: string;
}

export interface SlackDmSendResponse {
  results: SlackDmResult[];
  sent: number;
  failed: number;
}

export interface SlackDmInput {
  template: string;
  /** Per-person variables, keyed by email. Matched case-insensitively. */
  values?: Record<string, Record<string, string>>;
  /** Restrict to these addresses. Empty means every enrolled student. */
  emails?: string[];
  /** Staff are left out unless asked for. */
  include_staff?: boolean;
}

/**
 * Variables always available to a DM template, from the student's own record.
 *
 * These cannot be overridden by uploaded values, which inverts the usual
 * precedence. The reason is `api_key`: a values file exported last term and reused
 * would send every student a stale or revoked key in a message reading as entirely
 * correct.
 */
export const DM_BUILTIN_VARIABLES = [
  "name",
  "email",
  "api_key",
  "team",
  "course",
  "term",
] as const;

export const adminApi = {
  terms: (adminKey: string) => request<Term[]>("/admin/terms", {}, { adminKey }),

  /**
   * Check the Slack token and report which workspace it belongs to.
   *
   * Worth calling on its own: every other Slack failure is ambiguous between a bad
   * token, a missing scope, and a channel the app was never invited to.
   */
  slackAuth: (adminKey: string) =>
    request<SlackAuth>("/admin/slack/auth", {}, { adminKey }),

  /** Compare the workspace with a course's enrollment, both ways. Writes nothing. */
  slackSync: (courseId: string, adminKey: string) =>
    request<SlackSyncPreview>(
      `/admin/slack/sync?course_id=${encodeURIComponent(courseId)}`,
      {},
      { adminKey },
    ),

  /**
   * Commit confirmed actions. The server re-checks each against the database and
   * skips, with a reason, anything that no longer applies; it does not consult Slack.
   */
  slackSyncApply: (courseId: string, actions: SlackSyncAction[], adminKey: string) =>
    request<SlackSyncApplyResponse>(
      `/admin/slack/sync/apply?course_id=${encodeURIComponent(courseId)}`,
      { method: "POST", ...jsonBody({ actions }) },
      { adminKey },
    ),

  /** Render the template for every recipient. Sends nothing. */
  slackDmPreview: (courseId: string, input: SlackDmInput, adminKey: string) =>
    request<SlackDmPreview>(
      `/admin/slack/dm/preview?course_id=${encodeURIComponent(courseId)}`,
      { method: "POST", ...jsonBody(input) },
      { adminKey },
    ),

  /**
   * Send the messages.
   *
   * Takes the same input as the preview, not the rendered text: the server
   * re-renders, so what goes out is what the preview computed.
   */
  slackDmSend: (courseId: string, input: SlackDmInput, adminKey: string) =>
    request<SlackDmSendResponse>(
      `/admin/slack/dm/send?course_id=${encodeURIComponent(courseId)}`,
      { method: "POST", ...jsonBody(input) },
      { adminKey },
    ),

  createTerm: (
    input: { id: string; label: string; starts_at: number; active?: boolean },
    adminKey: string,
  ) =>
    request<Term>(
      "/admin/terms",
      { method: "POST", ...jsonBody(input) },
      { adminKey },
    ),

  /**
   * Setting `active: false` retires every API key in the term at once — a key is
   * valid only while its key, course and term are all active. This is how a
   * semester is ended; leaderboards stay readable.
   */
  patchTerm: (
    id: string,
    patch: { label?: string; starts_at?: number; active?: boolean },
    adminKey: string,
  ) =>
    request<Term>(
      `/admin/terms/${encodeURIComponent(id)}`,
      { method: "PATCH", ...jsonBody(patch) },
      { adminKey },
    ),

  courses: (adminKey: string, termId?: string) =>
    request<Course[]>(
      `/admin/courses${termId ? `?term_id=${encodeURIComponent(termId)}` : ""}`,
      {},
      { adminKey },
    ),

  createCourse: (
    input: { term_id: string; code: string; title?: string; team_max_size?: number; teams_open?: boolean; teams_lock_at?: number | null },
    adminKey: string,
  ) =>
    request<Course>(
      "/admin/courses",
      { method: "POST", ...jsonBody(input) },
      { adminKey },
    ),

  patchCourse: (
    id: string,
    patch: { code?: string; title?: string; active?: boolean; team_max_size?: number; teams_open?: boolean; teams_lock_at?: number | null },
    adminKey: string,
  ) =>
    request<Course>(
      `/admin/courses/${encodeURIComponent(id)}`,
      { method: "PATCH", ...jsonBody(patch) },
      { adminKey },
    ),

  /**
   * The destructive trio -- term, course, enrollment -- share one contract: DELETE
   * without `confirm` answers 428 with a sentence describing what would be destroyed,
   * which the dialog shows instead of "are you sure?"; DELETE with `confirm` equal to
   * the id (the email, for an enrollment) does it. `previewDelete*` is the first call.
   */
  previewDeleteTerm: (id: string, adminKey: string) => previewDelete(`/admin/terms/${encodeURIComponent(id)}`, adminKey),
  deleteTerm: (id: string, adminKey: string) =>
    request<{ deleted: string }>(
      `/admin/terms/${encodeURIComponent(id)}?confirm=${encodeURIComponent(id)}`,
      { method: "DELETE" },
      { adminKey },
    ),
  previewDeleteCourse: (id: string, adminKey: string) => previewDelete(`/admin/courses/${encodeURIComponent(id)}`, adminKey),
  deleteCourse: (id: string, adminKey: string) =>
    request<{ deleted: string }>(
      `/admin/courses/${encodeURIComponent(id)}?confirm=${encodeURIComponent(id)}`,
      { method: "DELETE" },
      { adminKey },
    ),
  previewDeleteStudent: (studentId: number, adminKey: string) => previewDelete(`/admin/students/${studentId}`, adminKey),
  deleteStudent: (studentId: number, email: string, adminKey: string) =>
    request<{ deleted: string }>(
      `/admin/students/${studentId}?confirm=${encodeURIComponent(email)}`,
      { method: "DELETE" },
      { adminKey },
    ),

  /**
   * Also serves as credential validation — the cheapest admin-only GET.
   *
   * A list, not a map keyed by the secret: the old shape put the credential into
   * every log line that echoed a response body.
   */
  keys: (adminKey: string, courseId?: string) =>
    request<StudentKeyEntry[]>(
      `/admin/keys${courseId ? `?course_id=${encodeURIComponent(courseId)}` : ""}`,
      {},
      { adminKey },
    ),

  /** Enrols students in a course and issues each a key. Idempotent by email. */
  generateKeys: (
    courseId: string,
    entries: { email: string; name?: string }[],
    adminKey: string,
  ) =>
    request<KeyGenerateResponse>(
      `/admin/keys/generate?course_id=${encodeURIComponent(courseId)}`,
      { method: "POST", ...jsonBody(entries) },
      { adminKey },
    ),

  patchKey: (
    key: string,
    patch: { active?: boolean; name?: string; role?: StudentRole },
    adminKey: string,
  ) =>
    request<StudentKeyEntry>(
      `/admin/keys/${encodeURIComponent(key)}`,
      { method: "PATCH", ...jsonBody(patch) },
      { adminKey },
    ),

  challenges: (adminKey: string, courseId?: string) =>
    request<AdminChallenge[]>(
      `/admin/challenges${courseId ? `?course_id=${encodeURIComponent(courseId)}` : ""}`,
      {},
      { adminKey },
    ),

  challenge: (id: string, adminKey: string) =>
    request<AdminChallenge>(`/admin/challenges/${id}`, {}, { adminKey }),

  createChallenge: (input: CreateChallengeInput, adminKey: string) => {
    const form = new FormData();
    form.append("course_id", input.course_id);
    form.append("name", input.name);
    form.append("type", input.type);
    form.append("simulator", input.simulator);
    form.append("visibility", input.visibility ?? "enrolled");
    form.append("maze_id", input.maze_id ?? "");
    form.append("description", input.description);
    form.append("exploration_data_url", input.exploration_data_url ?? "");
    if (input.maze_file) form.append("maze_file", input.maze_file);
    form.append("show_trajectory", String(input.show_trajectory ?? false));
    form.append("collects_report", String(input.collects_report ?? false));
    if (input.opens_at != null) form.append("opens_at", String(input.opens_at));
    form.append("final_submission_link", input.final_submission_link ?? "");
    if (input.rubric !== undefined) form.append("rubric", input.rubric);
    if (input.deadline !== undefined && input.deadline !== null)
      form.append("deadline", String(input.deadline));
    // The server refuses these four for a client-simulated challenge, even at their
    // defaults, so they are only sent for the mode they belong to. Until this was
    // added the dialog collected the limits and then dropped them on the floor:
    // every challenge was created with the defaults and had to be edited.
    if (input.simulator === "server") {
      if (input.bundle_id) form.append("bundle_id", input.bundle_id);
      if (input.max_attempts !== undefined && input.max_attempts !== null)
        form.append("max_attempts", String(input.max_attempts));
      if (input.max_concurrent_sessions !== undefined)
        form.append("max_concurrent_sessions", String(input.max_concurrent_sessions));
      if (input.max_steps !== undefined)
        form.append("max_steps", String(input.max_steps));
      if (input.motion_noise !== undefined)
        form.append("motion_noise", String(input.motion_noise));
      if (input.noise_linear_sigma !== undefined)
        form.append("noise_linear_sigma", String(input.noise_linear_sigma));
      if (input.noise_angular_sigma !== undefined)
        form.append("noise_angular_sigma", String(input.noise_angular_sigma));
      if (input.noise_speed_scale_sigma !== undefined)
        form.append("noise_speed_scale_sigma", String(input.noise_speed_scale_sigma));
      if (input.noise_turn_scale_sigma !== undefined)
        form.append("noise_turn_scale_sigma", String(input.noise_turn_scale_sigma));
      if (input.noise_heading_bias_sigma !== undefined)
        form.append("noise_heading_bias_sigma", String(input.noise_heading_bias_sigma));
    }
    return request<AdminChallenge>(
      "/admin/challenges",
      { method: "POST", body: form },
      { adminKey },
    );
  },

  /** Every attempt at a challenge, with both timing measurements. */
  adminSimSessions: (challengeId: string, adminKey: string) =>
    request<AdminSimSession[]>(
      `/admin/challenges/${challengeId}/sessions`,
      {},
      { adminKey },
    ),

  /**
   * End a running session: `expired`, "ended by an admin". The worker's session is closed
   * and the client is told. Does not score anything and does not refund the attempt.
   */
  endSimSession: (challengeId: string, sessionId: string, adminKey: string) =>
    request<AdminSimSession>(
      `/admin/challenges/${challengeId}/sessions/${sessionId}/end`,
      { method: "POST" },
      { adminKey },
    ),

  // --- the maze library: see routers/admin_mazes.py ---

  mazes: (adminKey: string) => request<Maze[]>("/admin/mazes", {}, { adminKey }),

  maze: (id: string, adminKey: string) => request<Maze>(`/admin/mazes/${id}`, {}, { adminKey }),

  generateMaze: (input: GenerateMazeInput, adminKey: string) =>
    request<Maze>("/admin/mazes/generate", { method: "POST", ...jsonBody(input) }, { adminKey }),

  /** What `generateMaze` would make from the same input; nothing is written. */
  previewMaze: (input: GenerateMazeInput, adminKey: string, signal?: AbortSignal) =>
    request<MazePreview>("/admin/mazes/preview", { method: "POST", ...jsonBody(input), signal }, { adminKey }),

  patchMaze: (id: string, patch: { name?: string; notes?: string }, adminKey: string) => {
    const query = new URLSearchParams();
    if (patch.name !== undefined) query.set("name", patch.name);
    if (patch.notes !== undefined) query.set("notes", patch.notes);
    return request<Maze>(`/admin/mazes/${id}?${query}`, { method: "PATCH" }, { adminKey });
  },

  deleteMaze: (id: string, adminKey: string) =>
    request<{ deleted: string }>(`/admin/mazes/${id}`, { method: "DELETE" }, { adminKey }),

  plan: (mazeId: string, input: PlanInput, adminKey: string) =>
    request<Plan>(`/admin/mazes/${mazeId}/plan`, { method: "POST", ...jsonBody(input) }, { adminKey }),

  dataset: (mazeId: string, adminKey: string) =>
    request<DatasetStatus>(`/admin/mazes/${mazeId}/dataset`, {}, { adminKey }),

  recordDataset: (mazeId: string, input: RecordInput, adminKey: string) =>
    request<DatasetStatus>(
      `/admin/mazes/${mazeId}/dataset/record`,
      { method: "POST", ...jsonBody(input) },
      { adminKey },
    ),

  deleteDataset: (mazeId: string, adminKey: string) =>
    request<DatasetStatus>(`/admin/mazes/${mazeId}/dataset`, { method: "DELETE" }, { adminKey }),

  // --- the worker behind a challenge: see routers/admin_sim.py ---

  worker: (id: string, adminKey: string) =>
    request<WorkerStatus>(`/admin/challenges/${id}/worker`, {}, { adminKey }),

  startWorker: (id: string, options: WorkerOptionsInput | null, adminKey: string) =>
    request<WorkerStatus>(
      `/admin/challenges/${id}/worker/start`,
      { method: "POST", ...(options ? jsonBody(options) : {}) },
      { adminKey },
    ),

  stopWorker: (id: string, adminKey: string) =>
    request<WorkerStatus>(`/admin/challenges/${id}/worker/stop`, { method: "POST" }, { adminKey }),

  /**
   * An authenticated image, as an object URL. `<img src>` cannot send a header, and putting
   * the admin key in a query string would land it in every proxy log between here and the
   * server; fetching the bytes and handing back a blob URL keeps it in the header.
   */
  fetchBlob: async (path: string, adminKey: string): Promise<string> => {
    const response = await fetch(`${API_URL}${path}`, { headers: authHeaders({ adminKey }) });
    if (!response.ok) throw new ApiError(response.status, `HTTP ${response.status}`);
    return URL.createObjectURL(await response.blob());
  },

  /** Parse a draft rubric and score sample runs under it, as the TA types. */
  checkRubric: (text: string, simulator: SimulatorSide, adminKey: string) =>
    request<RubricCheck>(
      "/admin/challenges/rubric/check",
      { method: "POST", ...jsonBody({ text, simulator }) },
      { adminKey },
    ),

  patchChallenge: (id: string, patch: PatchChallengeInput, adminKey: string) =>
    request<AdminChallenge>(
      `/admin/challenges/${id}`,
      { method: "PATCH", ...jsonBody(patch) },
      { adminKey },
    ),

  /**
   * Irreversibly deletes the challenge and every submission row for it. The
   * server requires `confirm` to equal the challenge id.
   *
   * Recordings are left on disk so a mistake stays recoverable; `tools/fsck.py
   * --prune` removes them server-side.
   */
  deleteChallenge: (id: string, adminKey: string) =>
    request<{ deleted: string }>(
      `/admin/challenges/${id}?confirm=${encodeURIComponent(id)}`,
      { method: "DELETE" },
      { adminKey },
    ),

  /** Dry run: returns the 428 message describing what would be destroyed. */
  previewDeleteChallenge: (id: string, adminKey: string) => previewDelete(`/admin/challenges/${id}`, adminKey),

  /** A flat list, newest first, including failures. */
  challengeSubmissions: (id: string, adminKey: string) =>
    request<AdminSubmission[]>(`/admin/challenges/${id}/submissions`, {}, { adminKey }),

  challengeWalls: (id: string, adminKey: string) =>
    request<WallPanel[]>(`/admin/challenges/${id}/walls`, {}, { adminKey }),

  trajectory: (jobId: string, adminKey: string, maxPoints?: number) =>
    request<Trajectory>(
      `/admin/trajectory/${jobId}/data${maxPoints === undefined ? "" : `?max_points=${maxPoints}`}`,
      {},
      { adminKey },
    ),

  /** Every report in one course, with the roster pivoted against them. */
  reports: (courseId: string, adminKey: string) =>
    request<ReportsOverview>(
      `/admin/reports?course_id=${encodeURIComponent(courseId)}`,
      {},
      { adminKey },
    ),

  /** One report's PDF as an object URL; revoke it when done. */
  reportFileUrl: (reportId: string, adminKey: string) =>
    fileUrl(`/admin/reports/${reportId}/file`, { adminKey }),

  /** One report's code as an object URL; revoke it when done. */
  reportCodeUrl: (reportId: string, adminKey: string) =>
    fileUrl(`/admin/reports/${reportId}/code`, { adminKey }),

  previewDeleteReport: (reportId: string, adminKey: string) =>
    previewDelete(`/admin/reports/${reportId}`, adminKey),

  /** The report with its PDF and code. The team may then hand in afresh (late, after the deadline). */
  deleteReport: (reportId: string, adminKey: string) =>
    request<{ deleted: string }>(
      `/admin/reports/${reportId}?confirm=${encodeURIComponent(reportId)}`,
      { method: "DELETE" },
      { adminKey },
    ),

  /**
   * Teams in one course. `courseId` is required, not optional: team names are
   * unique only within a course, so an unscoped list would show several
   * indistinguishable "Team 1" rows once a second cohort exists.
   */
  groups: (courseId: string, adminKey: string) =>
    request<AdminGroup[]>(
      `/admin/groups?course_id=${encodeURIComponent(courseId)}`,
      {},
      { adminKey },
    ),

  /** Rejected with 409 if the name is already taken in this course. */
  createGroup: (courseId: string, name: string, adminKey: string, studentIds: number[] = []) =>
    request<AdminGroup>(
      "/admin/groups",
      { method: "POST", ...jsonBody({ course_id: courseId, name, student_ids: studentIds }) },
      { adminKey },
    ),

  /** Put one student on the team; someone on another team is moved. Not capped for admins. */
  addGroupMember: (id: string, studentId: number, adminKey: string) =>
    request<AdminGroup>(`/admin/groups/${id}/members`, { method: "POST", ...jsonBody({ student_id: studentId }) }, { adminKey }),

  /** Take one student off. An emptied team is deleted, and `{deleted}` comes back instead. */
  removeGroupMember: (id: string, studentId: number, adminKey: string) =>
    request<AdminGroup | { deleted: string }>(`/admin/groups/${id}/members/${studentId}`, { method: "DELETE" }, { adminKey }),

  renameGroup: (id: string, name: string, adminKey: string) =>
    request<AdminGroup>(
      `/admin/groups/${id}`,
      { method: "PATCH", ...jsonBody({ name }) },
      { adminKey },
    ),

  deleteGroup: (id: string, adminKey: string) =>
    request<{ deleted: string }>(
      `/admin/groups/${id}`,
      { method: "DELETE" },
      { adminKey },
    ),

  /**
   * Replaces the roster wholesale. Listed students are removed from any other
   * group, so this can silently break up a different team.
   */
  setGroupMembers: (id: string, emails: string[], adminKey: string) =>
    request<AdminGroup>(
      `/admin/groups/${id}/members`,
      { method: "PUT", ...jsonBody({ emails }) },
      { adminKey },
    ),
};
